import React, { useMemo, useState, useEffect } from "react";
import TableRow from "@mui/material/TableRow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import { useForm, useFormState } from "react-hook-form";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ListItemIcon, ListItemText, useTheme } from "@mui/material";

import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { PageTitle } from "components/common/Text";
import CustomTable from "components/table";
import { TableBodyCell } from "components/table/table.style";
import {
  convertCognitoAttr,
  getCurrentPage,
  getFilteredListNew,
  getSortList,
  nameValidationCheck,
} from "utils/util-functions";
import { ActionBar, PageFooter } from "components/common/Wrapper";
import { PrimaryButton, GhostButton } from "components/common/Buttons";
import { FormInput, FieldError, PhoneInputWrapper } from "components/common/Input";
import { emailRegex } from "utils/constants";
import { addPowerUser, getPowerUserList } from "api/PowerUserHelpers";
import { deleteUser } from "api/AdminHelpers";
import { useAuth } from "hooks/useProvideAuth";
import { showSuccess } from "utils/notifications";
import useConfirm from "hooks/useConfirm";
import TableFilterNew from "components/table/TableFilterNew.component";
import POWER_USER_COLUMNS from "./PowerUsers.config";

const PowerUsersPage = () => {
  const [isOpenNewUserModal, setIsOpenNewUserModal] = useState(false);
  const [phoneNum, setPhoneNum] = useState();
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false);
  const [userList, setUserList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [filterColumn, setFilterColumn] = useState();
  const [filterType, setFilterType] = useState();
  const [filterWord, setFilterWord] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [sortBy, setSortBy] = useState();

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const open = Boolean(anchorEl);

  const { sub: userId } = useAuth();
  const theme = useTheme();
  const [getConfirmation, Confirmation] = useConfirm();

  const filterColumns = useMemo(() => POWER_USER_COLUMNS.filter(({ filter }) => filter), []);

  const filteredList = useMemo(
    () => getFilteredListNew(userList, filterColumn, filterType, filterWord),
    [userList, filterColumn, filterWord, filterType],
  );

  const sortedList = useMemo(() => {
    const sortColumn = POWER_USER_COLUMNS.find(({ id }) => id === sortBy);
    return getSortList(filteredList, sortBy, sortOrder, sortColumn?.type);
  }, [sortBy, sortOrder, filteredList]);

  const totalUserCount = useMemo(() => filteredList?.length || 0, [filteredList]);
  const paginatedList = useMemo(() => getCurrentPage(sortedList, page, rowsPerPage), [sortedList, page, rowsPerPage]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm();
  const formState = useFormState({ control });

  const powerUserColumns = useMemo(() => {
    const tempCols = [...POWER_USER_COLUMNS];
    tempCols.pop();
    return tempCols;
  }, []);

  const fetchPowerUsers = async (token) => {
    try {
      setIsLoading(true);
      const { data } = await getPowerUserList({ username: userId, token });
      const newUsers = (data?.Users ?? []).map((user) => ({
        ...user,
        ...convertCognitoAttr(user.Attributes || {}),
      }));
      if (token) {
        setUserList((value) => [...value, ...newUsers]);
      } else {
        setUserList(newUsers);
      }

      if (data.PaginationToken) {
        await fetchPowerUsers(data.PaginationToken);
      }
    } catch (error) {
      setUserList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPowerUsers();
  }, []);

  const onNewPowerUser = () => {
    setValue("userName", "");
    setValue("email", "");
    setPhoneNum("");

    setIsOpenNewUserModal(true);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
    setCurrentRow(null);
  };

  const onModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setIsOpenNewUserModal(false);
    }
  };

  const onCreateNewUser = async ({ email, userName }) => {
    if (!phoneNum || !isPossiblePhoneNumber(phoneNum)) return;

    setIsCreatingNewUser(true);
    try {
      const { data: response } = await addPowerUser({
        username: userId,
        name: userName,
        phonenumber: phoneNum,
        email: email,
      });
      showSuccess(response?.message);

      fetchPowerUsers();
      setIsOpenNewUserModal(false);
    } catch (error) {
      // error handling
    } finally {
      setIsCreatingNewUser(false);
    }
  };

  const closeModal = () => {
    setIsOpenNewUserModal(false);
  };

  const onClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setCurrentRow(row);
  };

  const onDeleteUser = async () => {
    setAnchorEl(null);

    const isConfirm = await getConfirmation({
      title: "Delete Power User",
      content: "Are you sure that you want to delete the selected power user?",
    });

    if (isConfirm) {
      try {
        const { data: response } = await deleteUser({
          username: userId,
          userEmail: currentRow?.email,
        });
        showSuccess(response?.message);
        fetchPowerUsers();
      } catch (error) {
        // error handling
      }
    }
  };

  const onSearch = (column, columnType, searchContent) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
  };

  const onResetFilter = () => {
    setFilterColumn();
    setFilterWord();
    setFilterType();
    setPage(0);
  };

  const handleRequestSort = (order, orderBy) => {
    setSortOrder(order);
    setSortBy(orderBy);
  };

  return (
    <>
      <PageTitle>Power Users</PageTitle>

      <ActionBar style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />

        <NxtIconButton onClick={onNewPowerUser} icon={<AddCircleOutlineIcon />} text="New Power User" />
      </ActionBar>

      <CustomTable
        rowsPerPageOptions={[10, 25, 100]}
        totalCount={totalUserCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(pageNum) => setPage(pageNum)}
        onRowsPerPageChange={(rowPerPage) => setRowsPerPage(rowPerPage)}
      >
        <CustomTable.Header columns={POWER_USER_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={paginatedList}>
          {paginatedList &&
            paginatedList.map(
              (row) =>
                row && (
                  <TableRow key={row.Username}>
                    {powerUserColumns.map((column) => {
                      const value = row[column.id];

                      return (
                        <TableBodyCell key={column.id}>{column.format ? column.format(value) : value}</TableBodyCell>
                      );
                    })}

                    <TableBodyCell style={{ width: 30 }}>
                      <Button onClick={(e) => onClickMenu(e, row)} style={{ color: theme.palette.common.black }}>
                        <MoreVertIcon />
                      </Button>
                    </TableBodyCell>
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={onDeleteUser}>
          <ListItemIcon>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog fullWidth maxWidth="xs" open={isOpenNewUserModal} onClose={onModalClose} disableEscapeKeyDown>
        <DialogTitle>New Power User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <FormInput
              {...register("userName", {
                required: "PowerUser Name is required",
                validate: {
                  nameValidationCheck,
                },
              })}
              placeholder="Power User Name"
              error={errors.userName}
            />
            {errors.userName && <FieldError>{errors.userName.message}</FieldError>}

            <FormInput
              placeholder="Email"
              {...register("email", {
                required: "Email is required!",
                pattern: {
                  value: emailRegex,
                  message: "Entered value does not match email format!",
                },
              })}
              style={{ marginTop: 20 }}
              error={errors.email}
            />
            {errors.email && <FieldError>{errors.email.message}</FieldError>}

            <PhoneInputWrapper
              international
              defaultCountry="US"
              countryCallingCodeEditable={false}
              value={phoneNum}
              onChange={(e) => {
                setPhoneNum(e);
              }}
            />
            {formState?.submitCount > 0 && (
              <FieldError>
                {phoneNum
                  ? isPossiblePhoneNumber(phoneNum)
                    ? undefined
                    : "Phone number is not valid!"
                  : "Phone number is required!"}
              </FieldError>
            )}

            <PageFooter>
              <PrimaryButton onClick={handleSubmit(onCreateNewUser)} loading={isCreatingNewUser}>
                Create
              </PrimaryButton>
              <GhostButton onClick={closeModal} sx={{ ml: 2 }}>
                Cancel
              </GhostButton>
            </PageFooter>
          </Box>
        </DialogContent>
      </Dialog>

      <Confirmation />
    </>
  );
};

export default PowerUsersPage;
